import { createStore } from 'vuex'
import { userApi } from '@/api'
import { doctorApi } from '@/api/doctor'

// 根据角色ID获取角色名称
const getRoleName = (roleId) => {
  switch (roleId) {
    case 1:
      return '医生'
    case 2:
      return '患者'
    case 3:
      return '管理员'
    default:
      return '未知角色'
  }
}

// 从localStorage获取用户信息
const getStoredUser = () => {
  try {
    const user = localStorage.getItem('cbti_user')
    return user ? JSON.parse(user) : null
  } catch (error) {
    console.error('获取用户信息失败:', error)
    return null
  }
}

// 从localStorage获取token
const getStoredToken = () => {
  try {
    return localStorage.getItem('cbti_token') || null
  } catch (error) {
    console.error('获取token失败:', error)
    return null
  }
}

// 从localStorage获取医生信息
const getStoredDoctorProfile = () => {
  try {
    const profile = localStorage.getItem('cbti_doctor_profile')
    return profile ? JSON.parse(profile) : null
  } catch (error) {
    console.error('获取医生信息失败:', error)
    return null
  }
}

// 从localStorage获取医生ID
const getStoredDoctorId = () => {
  try {
    return localStorage.getItem('cbti_doctor_id') || null
  } catch (error) {
    console.error('获取医生ID失败:', error)
    return null
  }
}

export default createStore({
  state: {
    user: getStoredUser(),
    token: getStoredToken(),
    isAuthenticated: !!getStoredUser() && !!getStoredToken(),
    doctorProfile: getStoredDoctorProfile(),
    doctorId: getStoredDoctorId(),
    // 共享时间筛选器状态
    sharedTimeFilter: {
      startDate: '',
      endDate: '',
      lastUpdatedBy: null, // 记录最后更新的页面
      lastUpdatedTime: null
    }
  },
  getters: {
    getUser: state => state.user,
    getToken: state => state.token,
    isAuthenticated: state => state.isAuthenticated,
    getDoctorProfile: state => state.doctorProfile,
    getDoctorId: state => state.doctorId,
    getCurrentDoctorId: state => {
      // 优先从医生信息中获取ID
      if (state.doctorProfile && state.doctorProfile.id) {
        return state.doctorProfile.id.toString()
      }
      // 其次从单独存储的医生ID中获取
      if (state.doctorId) {
        return state.doctorId.toString()
      }
      // 最后尝试从用户信息中获取
      if (state.user && state.user.id) {
        return state.user.id.toString()
      }
      return null
    },
    // 共享时间筛选器的 getters
    getSharedTimeFilter: state => state.sharedTimeFilter,
    getSharedStartDate: state => state.sharedTimeFilter.startDate,
    getSharedEndDate: state => state.sharedTimeFilter.endDate,
    getSharedDateRange: state => {
      const { startDate, endDate } = state.sharedTimeFilter
      return startDate && endDate ? [startDate, endDate] : []
    }
  },
  mutations: {
    setUser(state, user) {
      state.user = user
      state.isAuthenticated = !!user && !!state.token

      // 保存到localStorage
      if (user) {
        localStorage.setItem('cbti_user', JSON.stringify(user))
      } else {
        localStorage.removeItem('cbti_user')
      }
    },
    setToken(state, token) {
      state.token = token
      state.isAuthenticated = !!state.user && !!token

      // 保存到localStorage
      if (token) {
        localStorage.setItem('cbti_token', token)
      } else {
        localStorage.removeItem('cbti_token')
      }
    },
    setDoctorProfile(state, profile) {
      console.log('=== setDoctorProfile mutation 执行 ===')
      console.log('接收到的 profile 数据:', profile)
      
      state.doctorProfile = profile
      
      // 保存到localStorage
      if (profile) {
        console.log('✅ 保存医生信息到 localStorage')
        localStorage.setItem('cbti_doctor_profile', JSON.stringify(profile))
        
        // 同时保存医生ID
        if (profile.id) {
          const doctorIdStr = profile.id.toString()
          state.doctorId = doctorIdStr
          localStorage.setItem('cbti_doctor_id', doctorIdStr)
          console.log('✅ 医生ID已设置:', doctorIdStr)
          console.log('state.doctorId:', state.doctorId)
          console.log('localStorage cbti_doctor_id:', localStorage.getItem('cbti_doctor_id'))
        } else {
          console.warn('⚠️ profile 中没有 id 字段!')
          console.log('profile 的所有字段:', Object.keys(profile))
        }
      } else {
        console.warn('⚠️ profile 为空，清除 localStorage')
        localStorage.removeItem('cbti_doctor_profile')
        localStorage.removeItem('cbti_doctor_id')
      }
      
      console.log('=== setDoctorProfile mutation 执行完成 ===')
    },
    setDoctorId(state, doctorId) {
      state.doctorId = doctorId
      
      // 保存到localStorage
      if (doctorId) {
        localStorage.setItem('cbti_doctor_id', doctorId.toString())
      } else {
        localStorage.removeItem('cbti_doctor_id')
      }
    },
    // 共享时间筛选器的 mutations
    setSharedTimeFilter(state, { startDate, endDate, updatedBy }) {
      state.sharedTimeFilter.startDate = startDate
      state.sharedTimeFilter.endDate = endDate
      state.sharedTimeFilter.lastUpdatedBy = updatedBy
      state.sharedTimeFilter.lastUpdatedTime = new Date().toISOString()
      
      // 保存到localStorage以便页面刷新后恢复
      localStorage.setItem('cbti_shared_time_filter', JSON.stringify({
        startDate,
        endDate,
        lastUpdatedBy: updatedBy,
        lastUpdatedTime: state.sharedTimeFilter.lastUpdatedTime
      }))
      
      console.log(`🕐 共享时间筛选器已更新 [${updatedBy}]:`, { startDate, endDate })
    },
    
    clearSharedTimeFilter(state) {
      state.sharedTimeFilter.startDate = ''
      state.sharedTimeFilter.endDate = ''
      state.sharedTimeFilter.lastUpdatedBy = null
      state.sharedTimeFilter.lastUpdatedTime = null
      
      localStorage.removeItem('cbti_shared_time_filter')
      console.log('🧹 共享时间筛选器已清空')
    },
    
    initSharedTimeFilter(state) {
      try {
        const saved = localStorage.getItem('cbti_shared_time_filter')
        if (saved) {
          const filterData = JSON.parse(saved)
          state.sharedTimeFilter = { ...filterData }
          console.log('🔄 共享时间筛选器已从缓存恢复:', filterData)
        }
      } catch (error) {
        console.error('恢复共享时间筛选器失败:', error)
      }
    },
    
    logout(state) {
      console.log('🚪 开始执行 store logout mutation')

      // 重置所有状态
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.doctorProfile = null
      state.doctorId = null

      // 重置共享时间筛选器
      state.sharedTimeFilter = {
        startDate: '',
        endDate: '',
        lastUpdatedBy: null,
        lastUpdatedTime: null
      }

      // 清理localStorage中的所有相关数据
      try {
        const keysToRemove = [
          'cbti_user',
          'cbti_token',
          'cbti_doctor_profile',
          'cbti_doctor_id',
          'cbti_shared_time_filter'
        ]

        // 清除指定的键
        keysToRemove.forEach(key => {
          localStorage.removeItem(key)
          console.log(`🧹 已清除 localStorage: ${key}`)
        })

        // 清除所有以 cbti_ 开头的键（防止遗漏）
        const allKeys = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('cbti_')) {
            allKeys.push(key)
          }
        }
        allKeys.forEach(key => {
          localStorage.removeItem(key)
          console.log(`🧹 已清除额外的 localStorage: ${key}`)
        })

      } catch (error) {
        console.error('清除 localStorage 时出错:', error)
      }

      // 清理患者相关的数据缓存
      // 通过全局事件通知所有组件清理患者数据
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('doctor-logout', {
          detail: { message: '医生退出登录，清理患者数据缓存' }
        }))
        console.log('🧹 已发送 doctor-logout 事件')
      }

      console.log('🧹 store logout mutation 执行完成')
    }
  },
  actions: {
    async login({ commit }, { phone, password }) {
      try {
        console.log('开始登录:', { phone, password: '******' })

        // 调用医生登录API
        const result = await userApi.login({ phone, password })

        if (result.success) {
          // 登录成功，获取token
          const token = result.data?.token

          if (!token) {
            console.error('登录响应中缺少token')
            return {
              success: false,
              message: '登录失败，响应数据不完整'
            }
          }

          // 保存token到store和localStorage
          commit('setToken', token)

          // 构建用户数据，包含接口返回的信息
          const userData = {
            id: result.data?.id,
            phone: phone,
            name: result.data?.name,
            roleId: result.data?.roleId,
            roleName: getRoleName(result.data?.roleId),
            loginTime: new Date().toISOString()
          }

          console.log('登录成功，用户信息:', userData)

          // 保存用户信息到store和localStorage
          commit('setUser', userData)

          // 登录成功后，获取更详细的用户信息和医生信息
          if (userApi.getUserInfo) {
            setTimeout(() => {
              this.dispatch('fetchUserInfo')
            }, 100)
          }
          
          // 登录成功后，立即获取当前医生的详细信息（同步等待）
          console.log('📡 开始获取医生详细信息...')
          try {
            const doctorProfileResult = await this.dispatch('fetchDoctorProfile')
            if (doctorProfileResult && doctorProfileResult.success) {
              console.log('✅ 医生信息获取成功:', doctorProfileResult.data)
              console.log('✅ 医生ID已缓存:', doctorProfileResult.data?.id)
            } else {
              console.warn('⚠️ 医生信息获取失败:', doctorProfileResult?.message)
            }
          } catch (doctorError) {
            console.error('❌ 获取医生信息异常:', doctorError)
          }

          return {
            success: true,
            message: result.message || '登录成功',
            data: {
              name: userData.name,
              roleName: userData.roleName
            }
          }
        } else {
          // 登录失败，返回API的错误信息
          console.warn('登录失败:', result.message)
          return {
            success: false,
            message: result.message,
            errorCode: result.errorCode
          }
        }
      } catch (error) {
        console.error('登录失败:', error)
        return {
          success: false,
          message: error.message || '登录失败，请检查网络连接'
        }
      }
    },

    // 获取用户详细信息
    async fetchUserInfo({ commit, state }) {
      try {
        // 如果没有token，则不获取用户信息
        if (!state.token) return

        // 调用获取用户信息API
        const result = await userApi.getUserInfo()

        if (result.success && result.data) {
          // 更新用户信息
          const updatedUserData = {
            ...state.user,
            ...result.data,
            // 确保这些字段不被覆盖
            token: state.token,
            loginTime: state.user?.loginTime || new Date().toISOString()
          }

          commit('setUser', updatedUserData)
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
      }
    },

    // 获取当前医生详细信息
    async fetchDoctorProfile({ commit, state }) {
      try {
        console.log('=== fetchDoctorProfile 开始执行 ===')
        console.log('当前 token:', state.token ? '存在' : '不存在')
        console.log('当前 state.doctorProfile:', state.doctorProfile)
        console.log('当前 state.doctorId:', state.doctorId)
        
        // 如果没有token，则不获取医生信息
        if (!state.token) {
          console.warn('⚠️ 没有token，无法获取医生信息')
          return {
            success: false,
            message: '未登录，无法获取医生信息'
          }
        }

        console.log('📡 开始调用 getCurrentDoctorProfile API...')
        
        // 调用获取当前医生信息API
        const result = await doctorApi.getCurrentDoctorProfile()

        console.log('📥 API 返回结果:', result)
        console.log('result.success:', result.success)
        console.log('result.data:', result.data)

        if (result.success && result.data) {
          console.log('✅ 获取医生信息成功!')
          console.log('医生信息详情:', JSON.stringify(result.data, null, 2))
          console.log('医生ID:', result.data.id)
          
          // 更新医生信息到store
          commit('setDoctorProfile', result.data)
          
          console.log('✅ 医生信息已保存到 store')
          console.log('更新后的 state.doctorProfile:', state.doctorProfile)
          console.log('更新后的 state.doctorId:', state.doctorId)
          console.log('localStorage doctorId:', localStorage.getItem('cbti_doctor_id'))
          
          return {
            success: true,
            message: result.message,
            data: result.data
          }
        } else {
          console.warn('⚠️ 获取医生信息失败:', result.message)
          console.log('失败的 result 对象:', result)
          return {
            success: false,
            message: result.message || '获取医生信息失败'
          }
        }
      } catch (error) {
        console.error('❌ 获取医生信息异常:', error)
        console.error('错误堆栈:', error.stack)
        return {
          success: false,
          message: error.message || '获取医生信息失败'
        }
      }
    },

    // 共享时间筛选器的 actions
    updateSharedTimeFilter({ commit }, { startDate, endDate, updatedBy }) {
      commit('setSharedTimeFilter', { startDate, endDate, updatedBy })
      
      // 发送全局事件通知其他组件更新
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('shared-time-filter-updated', {
          detail: { 
            startDate, 
            endDate, 
            updatedBy,
            timestamp: new Date().toISOString()
          }
        }))
      }
    },
    
    clearSharedTimeFilter({ commit }) {
      commit('clearSharedTimeFilter')
      
      // 发送全局事件通知其他组件清空筛选
      if (typeof window !== 'undefined' && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('shared-time-filter-cleared', {
          detail: { timestamp: new Date().toISOString() }
        }))
      }
    },
    
    initSharedTimeFilter({ commit }) {
      commit('initSharedTimeFilter')
    },

    async logout({ commit }) {
      console.log('🚪 开始执行 logout action')

      try {
        // 调用退出登录API
        console.log('📡 调用退出登录API')
        await userApi.logout()
        console.log('✅ 退出登录API调用成功')
      } catch (error) {
        console.error('❌ 退出登录API调用失败:', error)
        // API失败不影响本地清理
      }

      try {
        // 无论API调用是否成功，都要清除本地状态
        console.log('🧹 开始清除本地状态')
        commit('logout')
        console.log('✅ 本地状态清除完成')

        // 确保状态已经更新
        await new Promise(resolve => setTimeout(resolve, 100))

      } catch (error) {
        console.error('❌ 清除本地状态时出错:', error)
        throw error
      }

      console.log('🚪 logout action 执行完成')
    }
  },
  modules: {
    // 可以在这里添加其他模块
  }
})